import { useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { DateTime } from "luxon";
import { App } from "antd";

import BaseButton from "@/components_v2/Button/BaseButton";
import { BUTTON_VARIANTS } from "@/components_v2/Button/constants";
import {
  HookFormBaseDropdown,
  HookFormTextArea,
} from "@/components_v2/FormFields/ReactHookForm/fields";
import BaseModal from "@/components_v2/Modal/BaseModal";
import FormFieldWrapper from "@/components_v2/Wrapper/FormFieldWrapper";
import { useBanUserMutation } from "@/redux/service";
import { BAN_DURATIONS } from "@/domains/moderation/constants";
import {
  BanDurationKey,
  BanFormValues,
  BanUserModalProps,
} from "@/domains/moderation/types";

import "./styles.css";

const MODAL_WIDTH = 676;
const BAN_DATE_FORMAT = "d MMMM yyyy";

const DEFAULT_VALUES: BanFormValues = { duration: null, reason: "" };

const BanUserModal = ({ user, relatedCase, onClose }: BanUserModalProps) => {
  const { t } = useTranslation();
  const { notification, modal } = App.useApp();
  const [ban, { isLoading }] = useBanUserMutation();
  const [error, setError] = useState<string | null>(null);

  const schema = useMemo(
    () =>
      yup.object({
        duration: yup
          .mixed<BanDurationKey>()
          .oneOf(BAN_DURATIONS.map(({ key }) => key))
          .nullable()
          .required(t("moderation.ban.durationRequired")),
        reason: yup
          .string()
          .trim()
          .required(t("moderation.ban.reasonRequired")),
      }),
    [t],
  );
  const form = useForm<BanFormValues>({
    resolver: yupResolver(schema) as never,
    defaultValues: DEFAULT_VALUES,
  });
  const duration = useWatch({ control: form.control, name: "duration" });

  useEffect(() => {
    form.reset(DEFAULT_VALUES);
    setError(null);
  }, [user, form]);

  if (!user) {
    return null;
  }

  const name = user.fullName.trim() || user.email;
  const firstName = user.firstName.trim() || name;

  const close = () => {
    if (isLoading) {
      return;
    }
    if (!form.getValues("reason").trim()) {
      onClose();
      return;
    }
    modal.confirm({
      title: t("moderation.ban.discardTitle"),
      content: t("moderation.ban.discardBody"),
      okText: t("moderation.ban.discard"),
      cancelText: t("moderation.ban.keepEditing"),
      onOk: onClose,
    });
  };

  const handleSubmit = form.handleSubmit(async (values) => {
    const days =
      BAN_DURATIONS.find(({ key }) => key === values.duration)?.days ?? null;
    const endsAt = days === null ? null : DateTime.now().plus({ days });
    setError(null);
    try {
      await ban({
        user: user.id,
        reason: values.reason.trim(),
        endsAt: endsAt?.toISO() ?? null,
        relatedCase,
      }).unwrap();
      notification.success({
        message: endsAt
          ? t("moderation.ban.bannedUntil", {
              name: firstName,
              date: endsAt.toFormat(BAN_DATE_FORMAT),
            })
          : t("moderation.ban.bannedPermanently", { name: firstName }),
      });
      onClose();
    } catch {
      setError(t("moderation.ban.error"));
    }
  });

  return (
    <BaseModal
      open
      width={MODAL_WIDTH}
      onCancel={close}
      maskClosable
      title={t("moderation.ban.title", { name })}
      footer={
        <div className="flex flex-col gap-300">
          {error && (
            <p className="ban-modal-error body-s-regular" role="alert">
              {error}
            </p>
          )}

          <div className="flex justify-end gap-200">
            <BaseButton
              variant={BUTTON_VARIANTS.TERTIARY}
              disabled={isLoading}
              onClick={close}
            >
              {t("buttons.cancel")}
            </BaseButton>

            <BaseButton
              variant={BUTTON_VARIANTS.DANGER}
              loading={isLoading}
              disabled={!duration}
              onClick={handleSubmit}
            >
              {t("moderation.ban.confirm")}
            </BaseButton>
          </div>
        </div>
      }
    >
      <form className="flex flex-col gap-400" onSubmit={handleSubmit}>
        <p className="ban-modal-line">
          {t("moderation.ban.line", { name: firstName })}
        </p>

        <FormFieldWrapper label={t("moderation.ban.durationLabel")}>
          <HookFormBaseDropdown
            name="duration"
            control={form.control}
            placeholder={t("moderation.ban.durationPlaceholder")}
            options={BAN_DURATIONS.map(({ key }) => ({
              value: key,
              label: t(`moderation.ban.durations.${key}`),
            }))}
          />
        </FormFieldWrapper>

        <FormFieldWrapper
          label={t("moderation.ban.reasonLabel", { name: firstName })}
        >
          <HookFormTextArea
            name="reason"
            control={form.control}
            rows={5}
            placeholder={t("moderation.ban.reasonPlaceholder")}
          />
        </FormFieldWrapper>
      </form>
    </BaseModal>
  );
};

export default BanUserModal;
